import { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { LogOut, Building2, ChevronDown, User, LayoutDashboard, Menu, X } from 'lucide-react';

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/portal' },
];

export default function PortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('portal_token');
  const client = JSON.parse(localStorage.getItem('portal_client') || '{}');
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  if (!token) {
    navigate('/portal/login');
    return null;
  }

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_client');
    navigate('/portal/login');
  };

  const initials = `${(client.first_name || '')[0] || ''}${(client.last_name || '')[0] || ''}`;
  const isActive = (path) => {
    if (path === '/portal') return location.pathname === '/portal';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="portal-layout">
      <PortalSidebar
        items={NAV_ITEMS}
        client={client}
        initials={initials}
        isActive={isActive}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        onLogout={handleLogout}
      />
      <div className="flex-1 flex flex-col min-h-screen">
        <PortalHeader
          client={client}
          initials={initials}
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          onLogout={handleLogout}
          navigate={navigate}
        />
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function PortalSidebar({ items, client, initials, isActive, sidebarOpen, setSidebarOpen, onLogout }) {
  return (
    <aside className={`bg-gradient-to-b from-[#0A1628] to-[#132743] text-white flex flex-col transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
      <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
        {sidebarOpen ? (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-[#84CC16] to-[#65a30d] rounded-lg flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-sm">Client Portal</span>
          </div>
        ) : (
          <div className="w-10 h-10 bg-gradient-to-br from-[#84CC16] to-[#65a30d] rounded-xl flex items-center justify-center flex-shrink-0 mx-auto">
            <Building2 className="w-5 h-5 text-white" />
          </div>
        )}
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1">
        {items.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
              isActive(item.path)
                ? 'bg-[#84CC16] text-white'
                : 'text-gray-400 hover:bg-white/10 hover:text-white'
            } ${!sidebarOpen ? 'justify-center' : ''}`}
          >
            <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive(item.path) ? 'text-white' : 'text-gray-400 group-hover:text-[#84CC16]'}`} />
            {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
          </Link>
        ))}
      </nav>

      <div className="p-3 border-t border-white/10">
        {sidebarOpen && (
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-9 h-9 bg-gradient-to-br from-[#84CC16] to-[#65a30d] rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{client.first_name} {client.last_name}</p>
              <p className="text-xs text-gray-400 truncate">{client.company_name}</p>
            </div>
          </div>
        )}
        <button
          onClick={onLogout}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors ${!sidebarOpen ? 'justify-center' : ''}`}
        >
          <LogOut className="w-5 h-5" />
          {sidebarOpen && <span className="text-sm">Logout</span>}
        </button>
      </div>
    </aside>
  );
}

function PortalHeader({ client, initials, menuOpen, setMenuOpen, onLogout, navigate }) {
  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-40">
      <h1 className="text-lg font-bold text-[#0A1628]">
        {client.company_name || `${client.first_name} ${client.last_name}`}
      </h1>
      <div className="relative">
        <button onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-2 hover:bg-gray-100 rounded-xl px-3 py-2 transition-colors">
          <div className="w-8 h-8 bg-gradient-to-br from-[#84CC16] to-[#65a30d] rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">{initials}</span>
          </div>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
            <button onClick={() => { setMenuOpen(false); navigate('/portal/profile'); }} className="w-full flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 text-sm">
              <User className="w-4 h-4" /> My Profile
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button onClick={() => { setMenuOpen(false); onLogout(); }} className="w-full flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 text-sm">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
